import { useLanguage } from "@/contexts/LanguageContext";

export type AppShellTranslations = {
  pageTitles: {
    companies: string;
  };
  /** Rótulos acessíveis dos componentes base (UiLabelsProvider no AppLayout). */
  a11y: {
    close: string;
  };
  header: {
    searchLabel: string;
    searchPlaceholder: string;
    organization: string;
    notifications: string;
    notificationsWithUnread: string;
    markAllRead: string;
    noNotifications: string;
    viewAllNotifications: string;
    userMenu: string;
    profile: string;
    settings: string;
    help: string;
    logout: string;
  };
  layout: {
    logoutSuccessTitle: string;
    logoutSuccessDescription: string;
    logoutErrorTitle: string;
    logoutErrorDescription: string;
    userFallback: string;
    organizationFallback: string;
    expandMenu: string;
    collapseMenu: string;
    partner: string;
    referral: string;
  };
  /** Sidebar v2 (docs/features/sidebar-v2/figma-spec.md §11). */
  sidebar: {
    nav: {
      dashboard: string;
      executive: string;
      companies: string;
      buyers: string;
      suppliers: string;
      portfolioUpdates: string;
      sectorRisk: string;
      riskMap: string;
      comparison: string;
      simulador360: string;
      reports: string;
      collections: string;
      billing: string;
      helpAndSupport: string;
    };
    groups: {
      overview: string;
      portfolio: string;
      riskAnalysis: string;
      finance: string;
    };
    roles: {
      owner: string;
      admin: string;
      collaborator: string;
      partner: string;
      support: string;
    };
    newQuery: string;
    orgTypeOrganization: string;
    orgTypePartner: string;
    distributor: string;
    userMenu: string;
    profile: string;
    orgSettings: string;
    logout: string;
    openMenu: string;
    closeMenu: string;
    mainNavigation: string;
    loading: string;
  };
  notVerified: {
    title: string;
    description: string;
  };
  supportRequest: {
    sentTitle: string;
    sentDescription: string;
    errorTitle: string;
    errorDescription: string;
    title: string;
    description: string;
    company: string;
    country: string;
    nationalId: string;
    cofaceId: string;
    dataApiId: string;
    iconNumber: string;
    notFound: string;
    send: string;
    cancel: string;
  };
  notificationsPage: {
    pageTitle: string;
    unreadSummary: string;
    allReadSummary: string;
    markAllRead: string;
    statusPlaceholder: string;
    typePlaceholder: string;
    all: string;
    unread: string;
    allTypes: string;
    emptyTitle: string;
    emptyUnreadDescription: string;
    emptyDescription: string;
    types: Record<string, string>;
  };
  helpPage: {
    pageTitle: string;
    title: string;
    subtitle: string;
    constructionTitle: string;
    constructionDescription: string;
  };
  supportPage: {
    title: string;
    newTicket: string;
    openTicket: string;
    reason: string;
    selectReason: string;
    subject: string;
    subjectPlaceholder: string;
    description: string;
    descriptionPlaceholder: string;
    sending: string;
    sendTicket: string;
    loadingTickets: string;
    noTickets: string;
    createSuccess: string;
    createError: string;
    sendMessageError: string;
    loading: string;
    ticketNotFound: string;
    loadingMessages: string;
    noMessages: string;
    support: string;
    you: string;
    messagePlaceholder: string;
    ticketClosed: string;
    resolved: string;
    closed: string;
    categories: Record<string, string>;
    statuses: Record<string, string>;
  };
};

const appShellTranslations: Record<"pt" | "en" | "es", AppShellTranslations> = {
  pt: {
    pageTitles: {
      companies: "Empresas",
    },
    a11y: {
      close: "Fechar",
    },
    header: {
      searchLabel: "Buscar",
      searchPlaceholder: "Buscar empresas, relatórios...",
      organization: "Organização",
      notifications: "Notificações",
      notificationsWithUnread: "Notificações: {count} não lidas",
      markAllRead: "Marcar todas como lidas",
      noNotifications: "Nenhuma notificação no momento",
      viewAllNotifications: "Ver todas as notificações",
      userMenu: "Menu do usuário",
      profile: "Perfil",
      settings: "Configurações",
      help: "Ajuda",
      logout: "Sair",
    },
    layout: {
      logoutSuccessTitle: "Logout realizado",
      logoutSuccessDescription: "Você foi desconectado com sucesso.",
      logoutErrorTitle: "Erro ao sair",
      logoutErrorDescription:
        "Ocorreu um erro ao tentar sair. Tente novamente.",
      userFallback: "Usuário",
      organizationFallback: "Minha Empresa",
      expandMenu: "Expandir menu",
      collapseMenu: "Recolher menu",
      partner: "Parceiro",
      referral: "Indicação",
    },
    sidebar: {
      nav: {
        dashboard: "Dashboard",
        executive: "Executive",
        companies: "Empresas",
        buyers: "Compradores",
        suppliers: "Fornecedores",
        portfolioUpdates: "Atualizações",
        sectorRisk: "Risco setorial",
        riskMap: "Mapa de risco",
        comparison: "Comparação",
        simulador360: "Simulador 360",
        reports: "Relatórios",
        collections: "Cobranças",
        billing: "Faturamento",
        helpAndSupport: "Ajuda e suporte",
      },
      groups: {
        overview: "Visão geral",
        portfolio: "Carteira",
        riskAnalysis: "Análise de risco",
        finance: "Financeiro",
      },
      roles: {
        owner: "Owner",
        admin: "Admin",
        collaborator: "Colaborador",
        partner: "Parceiro",
        support: "Suporte",
      },
      newQuery: "Nova consulta",
      orgTypeOrganization: "Organização",
      orgTypePartner: "Parceiro",
      distributor: "Distribuidor oficial",
      userMenu: "Menu do usuário",
      profile: "Meu perfil",
      orgSettings: "Configurações da organização",
      logout: "Sair",
      openMenu: "Abrir menu",
      closeMenu: "Fechar menu",
      mainNavigation: "Navegação principal",
      loading: "Carregando menu",
    },
    notVerified: {
      title: "Não verificado",
      description:
        "Os dados de {productLabel} estarão disponíveis após a compra de um relatório.",
    },
    supportRequest: {
      sentTitle: "Solicitação enviada",
      sentDescription:
        "Nossa equipe de suporte irá analisar a situação e entrará em contato.",
      errorTitle: "Erro ao enviar solicitação",
      errorDescription:
        "Tente novamente ou entre em contato diretamente pelo e-mail.",
      title: "Dados da empresa incompletos",
      description:
        "Esta empresa não possui o Icon Number necessário para solicitar relatórios URBA. Este caso precisa ser analisado pela equipe de suporte.",
      company: "Empresa:",
      country: "País:",
      nationalId: "ID nacional:",
      cofaceId: "ID Coface:",
      dataApiId: "ID da Data API:",
      iconNumber: "Icon Number:",
      notFound: "Não encontrado",
      send: "Enviar solicitação ao suporte",
      cancel: "Cancelar",
    },
    notificationsPage: {
      pageTitle: "Notificações",
      unreadSummary: "Você tem {count} notificação(ões) não lida(s)",
      allReadSummary: "Todas as notificações foram lidas",
      markAllRead: "Marcar todas como lidas",
      statusPlaceholder: "Status",
      typePlaceholder: "Tipo",
      all: "Todas",
      unread: "Não lidas",
      allTypes: "Todos os tipos",
      emptyTitle: "Nenhuma notificação",
      emptyUnreadDescription: "Todas as notificações foram lidas.",
      emptyDescription: "Você ainda não recebeu notificações.",
      types: {
        REPORT_COMPLETED: "Relatório concluído",
        RISK_RATING_CHANGED: "Rating alterado",
        SECTOR_RISK_CHANGED: "Risco setorial",
        COMPANY_RATING_CHANGED: "Rating da empresa",
        urba_monitoring_change: "Alteração de monitoramento",
        WELCOME_NEW_USER: "Boas-vindas",
        INVOICE_GENERATED: "Fatura gerada",
        PAYMENT_SUCCESS: "Pagamento confirmado",
        PAYMENT_FAILED: "Falha no pagamento",
        SUPPORT_MESSAGE: "Mensagem de suporte",
      },
    },
    helpPage: {
      pageTitle: "Ajuda",
      title: "Central de ajuda",
      subtitle: "Encontre respostas para suas dúvidas",
      constructionTitle: "Em construção",
      constructionDescription:
        "Estamos preparando uma central de ajuda completa com FAQ, tutoriais e guias para você aproveitar ao máximo a plataforma.",
    },
    supportPage: {
      title: "Suporte",
      newTicket: "Novo chamado",
      openTicket: "Abrir chamado",
      reason: "Motivo",
      selectReason: "Selecione o motivo",
      subject: "Assunto",
      subjectPlaceholder: "Resumo do problema",
      description: "Descrição",
      descriptionPlaceholder: "Descreva detalhadamente o problema...",
      sending: "Enviando...",
      sendTicket: "Enviar chamado",
      loadingTickets: "Carregando chamados...",
      noTickets: "Nenhum chamado aberto ainda.",
      createSuccess: "Chamado aberto com sucesso!",
      createError: "Erro ao abrir chamado. Tente novamente.",
      sendMessageError: "Erro ao enviar mensagem. Tente novamente.",
      loading: "Carregando...",
      ticketNotFound: "Chamado não encontrado.",
      loadingMessages: "Carregando mensagens...",
      noMessages: "Nenhuma mensagem ainda.",
      support: "Suporte",
      you: "Você",
      messagePlaceholder: "Digite sua mensagem...",
      ticketClosed: "Este chamado foi {status}.",
      resolved: "resolvido",
      closed: "fechado",
      categories: {
        problema_tecnico: "Problema técnico",
        duvida_relatorio: "Dúvida sobre relatório",
        faturamento: "Faturamento / cobrança",
        cadastro_empresa: "Cadastro de empresa",
        acesso_permissoes: "Acesso / permissões",
        outro: "Outro",
      },
      statuses: {
        aberto: "Aberto",
        em_andamento: "Em andamento",
        aguardando_cliente: "Aguardando cliente",
        resolvido: "Resolvido",
        fechado: "Fechado",
      },
    },
  },
  en: {
    pageTitles: {
      companies: "Companies",
    },
    a11y: {
      close: "Close",
    },
    header: {
      searchLabel: "Search",
      searchPlaceholder: "Search companies, reports...",
      organization: "Organization",
      notifications: "Notifications",
      notificationsWithUnread: "Notifications: {count} unread",
      markAllRead: "Mark all as read",
      noNotifications: "No notifications at the moment",
      viewAllNotifications: "View all notifications",
      userMenu: "User menu",
      profile: "Profile",
      settings: "Settings",
      help: "Help",
      logout: "Log out",
    },
    layout: {
      logoutSuccessTitle: "Logged out",
      logoutSuccessDescription: "You have been signed out successfully.",
      logoutErrorTitle: "Sign-out error",
      logoutErrorDescription:
        "An error occurred while signing out. Please try again.",
      userFallback: "User",
      organizationFallback: "My Company",
      expandMenu: "Expand menu",
      collapseMenu: "Collapse menu",
      partner: "Partner",
      referral: "Referral",
    },
    sidebar: {
      nav: {
        dashboard: "Dashboard",
        executive: "Executive",
        companies: "Companies",
        buyers: "Buyers",
        suppliers: "Suppliers",
        portfolioUpdates: "Updates",
        sectorRisk: "Sector risk",
        riskMap: "Risk map",
        comparison: "Comparison",
        simulador360: "Simulator 360",
        reports: "Reports",
        collections: "Collections",
        billing: "Billing",
        helpAndSupport: "Help & support",
      },
      groups: {
        overview: "Overview",
        portfolio: "Portfolio",
        riskAnalysis: "Risk analysis",
        finance: "Finance",
      },
      roles: {
        owner: "Owner",
        admin: "Admin",
        collaborator: "Collaborator",
        partner: "Partner",
        support: "Support",
      },
      newQuery: "New search",
      orgTypeOrganization: "Organization",
      orgTypePartner: "Partner",
      distributor: "Official distributor",
      userMenu: "User menu",
      profile: "My profile",
      orgSettings: "Organization settings",
      logout: "Log out",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      mainNavigation: "Main navigation",
      loading: "Loading menu",
    },
    notVerified: {
      title: "Not verified",
      description:
        "{productLabel} data will be available after purchasing a report.",
    },
    supportRequest: {
      sentTitle: "Request sent",
      sentDescription:
        "Our support team will review the situation and get in touch.",
      errorTitle: "Error sending request",
      errorDescription: "Try again or contact us directly by email.",
      title: "Incomplete company data",
      description:
        "This company does not have the Icon Number required to request URBA reports. This case must be reviewed by the support team.",
      company: "Company:",
      country: "Country:",
      nationalId: "National ID:",
      cofaceId: "Coface ID:",
      dataApiId: "Data API ID:",
      iconNumber: "Icon Number:",
      notFound: "Not found",
      send: "Send request to support",
      cancel: "Cancel",
    },
    notificationsPage: {
      pageTitle: "Notifications",
      unreadSummary: "You have {count} unread notification(s)",
      allReadSummary: "All notifications have been read",
      markAllRead: "Mark all as read",
      statusPlaceholder: "Status",
      typePlaceholder: "Type",
      all: "All",
      unread: "Unread",
      allTypes: "All types",
      emptyTitle: "No notifications",
      emptyUnreadDescription: "All notifications have been read.",
      emptyDescription: "You have not received any notifications yet.",
      types: {
        REPORT_COMPLETED: "Report completed",
        RISK_RATING_CHANGED: "Rating changed",
        SECTOR_RISK_CHANGED: "Sector risk",
        COMPANY_RATING_CHANGED: "Company rating",
        urba_monitoring_change: "Monitoring update",
        WELCOME_NEW_USER: "Welcome",
        INVOICE_GENERATED: "Invoice generated",
        PAYMENT_SUCCESS: "Payment confirmed",
        PAYMENT_FAILED: "Payment failed",
        SUPPORT_MESSAGE: "Support message",
      },
    },
    helpPage: {
      pageTitle: "Help",
      title: "Help center",
      subtitle: "Find answers to your questions",
      constructionTitle: "Under construction",
      constructionDescription:
        "We are preparing a complete help center with FAQs, tutorials and guides to help you make the most of the platform.",
    },
    supportPage: {
      title: "Support",
      newTicket: "New ticket",
      openTicket: "Open ticket",
      reason: "Reason",
      selectReason: "Select a reason",
      subject: "Subject",
      subjectPlaceholder: "Summary of the issue",
      description: "Description",
      descriptionPlaceholder: "Describe the issue in detail...",
      sending: "Sending...",
      sendTicket: "Send ticket",
      loadingTickets: "Loading tickets...",
      noTickets: "No open tickets yet.",
      createSuccess: "Ticket opened successfully!",
      createError: "Error opening ticket. Please try again.",
      sendMessageError: "Error sending message. Please try again.",
      loading: "Loading...",
      ticketNotFound: "Ticket not found.",
      loadingMessages: "Loading messages...",
      noMessages: "No messages yet.",
      support: "Support",
      you: "You",
      messagePlaceholder: "Type your message...",
      ticketClosed: "This ticket was {status}.",
      resolved: "resolved",
      closed: "closed",
      categories: {
        problema_tecnico: "Technical issue",
        duvida_relatorio: "Question about a report",
        faturamento: "Billing / collection",
        cadastro_empresa: "Company registration",
        acesso_permissoes: "Access / permissions",
        outro: "Other",
      },
      statuses: {
        aberto: "Open",
        em_andamento: "In progress",
        aguardando_cliente: "Waiting for customer",
        resolvido: "Resolved",
        fechado: "Closed",
      },
    },
  },
  es: {
    pageTitles: {
      companies: "Empresas",
    },
    a11y: {
      close: "Cerrar",
    },
    header: {
      searchLabel: "Buscar",
      searchPlaceholder: "Buscar empresas, informes...",
      organization: "Organización",
      notifications: "Notificaciones",
      notificationsWithUnread: "Notificaciones: {count} sin leer",
      markAllRead: "Marcar todas como leídas",
      noNotifications: "No hay notificaciones por el momento",
      viewAllNotifications: "Ver todas las notificaciones",
      userMenu: "Menú de usuario",
      profile: "Perfil",
      settings: "Configuración",
      help: "Ayuda",
      logout: "Cerrar sesión",
    },
    layout: {
      logoutSuccessTitle: "Sesión cerrada",
      logoutSuccessDescription: "Su sesión se cerró correctamente.",
      logoutErrorTitle: "Error al cerrar sesión",
      logoutErrorDescription:
        "Ocurrió un error al cerrar sesión. Inténtelo de nuevo.",
      userFallback: "Usuario",
      organizationFallback: "Mi empresa",
      expandMenu: "Expandir menú",
      collapseMenu: "Contraer menú",
      partner: "Socio",
      referral: "Referencia",
    },
    sidebar: {
      nav: {
        dashboard: "Dashboard",
        executive: "Executive",
        companies: "Empresas",
        buyers: "Compradores",
        suppliers: "Proveedores",
        portfolioUpdates: "Actualizaciones",
        sectorRisk: "Riesgo sectorial",
        riskMap: "Mapa de riesgo",
        comparison: "Comparativo",
        simulador360: "Simulador 360",
        reports: "Reportes",
        collections: "Cobranzas",
        billing: "Facturación",
        helpAndSupport: "Ayuda y soporte",
      },
      groups: {
        overview: "Visión general",
        portfolio: "Cartera",
        riskAnalysis: "Análisis de riesgo",
        finance: "Finanzas",
      },
      roles: {
        owner: "Owner",
        admin: "Admin",
        collaborator: "Colaborador",
        partner: "Socio",
        support: "Soporte",
      },
      newQuery: "Nueva consulta",
      orgTypeOrganization: "Organización",
      orgTypePartner: "Socio",
      distributor: "Distribuidor oficial",
      userMenu: "Menú de usuario",
      profile: "Mi perfil",
      orgSettings: "Configuración de la organización",
      logout: "Cerrar sesión",
      openMenu: "Abrir menú",
      closeMenu: "Cerrar menú",
      mainNavigation: "Navegación principal",
      loading: "Cargando menú",
    },
    notVerified: {
      title: "No verificado",
      description:
        "Los datos de {productLabel} estarán disponibles después de comprar un informe.",
    },
    supportRequest: {
      sentTitle: "Solicitud enviada",
      sentDescription:
        "Nuestro equipo de soporte analizará la situación y se pondrá en contacto.",
      errorTitle: "Error al enviar la solicitud",
      errorDescription:
        "Inténtelo de nuevo o contáctenos directamente por correo electrónico.",
      title: "Datos de la empresa incompletos",
      description:
        "Esta empresa no tiene el Icon Number necesario para solicitar informes URBA. Este caso debe ser analizado por el equipo de soporte.",
      company: "Empresa:",
      country: "País:",
      nationalId: "ID nacional:",
      cofaceId: "ID Coface:",
      dataApiId: "ID de Data API:",
      iconNumber: "Icon Number:",
      notFound: "No encontrado",
      send: "Enviar solicitud a soporte",
      cancel: "Cancelar",
    },
    notificationsPage: {
      pageTitle: "Notificaciones",
      unreadSummary: "Tiene {count} notificación(es) sin leer",
      allReadSummary: "Todas las notificaciones han sido leídas",
      markAllRead: "Marcar todas como leídas",
      statusPlaceholder: "Estado",
      typePlaceholder: "Tipo",
      all: "Todas",
      unread: "Sin leer",
      allTypes: "Todos los tipos",
      emptyTitle: "No hay notificaciones",
      emptyUnreadDescription: "Todas las notificaciones han sido leídas.",
      emptyDescription: "Aún no ha recibido notificaciones.",
      types: {
        REPORT_COMPLETED: "Informe completado",
        RISK_RATING_CHANGED: "Rating modificado",
        SECTOR_RISK_CHANGED: "Riesgo sectorial",
        COMPANY_RATING_CHANGED: "Rating de la empresa",
        urba_monitoring_change: "Actualización de monitoreo",
        WELCOME_NEW_USER: "Bienvenida",
        INVOICE_GENERATED: "Factura generada",
        PAYMENT_SUCCESS: "Pago confirmado",
        PAYMENT_FAILED: "Pago fallido",
        SUPPORT_MESSAGE: "Mensaje de soporte",
      },
    },
    helpPage: {
      pageTitle: "Ayuda",
      title: "Centro de ayuda",
      subtitle: "Encuentre respuestas a sus preguntas",
      constructionTitle: "En construcción",
      constructionDescription:
        "Estamos preparando un centro de ayuda completo con preguntas frecuentes, tutoriales y guías para que aproveche al máximo la plataforma.",
    },
    supportPage: {
      title: "Soporte",
      newTicket: "Nuevo ticket",
      openTicket: "Abrir ticket",
      reason: "Motivo",
      selectReason: "Seleccione el motivo",
      subject: "Asunto",
      subjectPlaceholder: "Resumen del problema",
      description: "Descripción",
      descriptionPlaceholder: "Describa el problema en detalle...",
      sending: "Enviando...",
      sendTicket: "Enviar ticket",
      loadingTickets: "Cargando tickets...",
      noTickets: "Aún no hay tickets abiertos.",
      createSuccess: "¡Ticket abierto con éxito!",
      createError: "Error al abrir el ticket. Inténtelo de nuevo.",
      sendMessageError: "Error al enviar el mensaje. Inténtelo de nuevo.",
      loading: "Cargando...",
      ticketNotFound: "Ticket no encontrado.",
      loadingMessages: "Cargando mensajes...",
      noMessages: "Aún no hay mensajes.",
      support: "Soporte",
      you: "Usted",
      messagePlaceholder: "Escriba su mensaje...",
      ticketClosed: "Este ticket fue {status}.",
      resolved: "resuelto",
      closed: "cerrado",
      categories: {
        problema_tecnico: "Problema técnico",
        duvida_relatorio: "Consulta sobre informe",
        faturamento: "Facturación / cobranza",
        cadastro_empresa: "Registro de empresa",
        acesso_permissoes: "Acceso / permisos",
        outro: "Otro",
      },
      statuses: {
        aberto: "Abierto",
        em_andamento: "En curso",
        aguardando_cliente: "Esperando al cliente",
        resolvido: "Resuelto",
        fechado: "Cerrado",
      },
    },
  },
};

export function useAppShellTranslations(): AppShellTranslations {
  const { language } = useLanguage();
  return appShellTranslations[language] ?? appShellTranslations.pt;
}
